import { useEffect, useState, useRef } from 'react';

/**
 * useFloorPlanTrigger
 *
 * Opens the Floor Plan popup when EITHER:
 *   - User has been on the page for `delayMs` (default 25s)
 *   - User has scrolled past `scrollThreshold` (default 0.5 = 50%)
 *
 * Suppressed for `suppressDays` (default 7) once the popup has been shown to the user.
 * Suppression is persisted in localStorage under `STORAGE_KEY`.
 *
 * Returns { open, setOpen, markShown } — caller controls open state, hook fires it.
 */
const STORAGE_KEY = 'nh_floorplan_popup_shown_at';

const wasRecentlyShown = (suppressDays) => {
  try {
    const ts = localStorage.getItem(STORAGE_KEY);
    if (!ts) return false;
    const shownAt = parseInt(ts, 10);
    if (Number.isNaN(shownAt)) return false;
    const ageMs = Date.now() - shownAt;
    return ageMs < suppressDays * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const markShownNow = () => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {/*ignore*/}
};

export const useFloorPlanTrigger = ({ delayMs = 25000, scrollThreshold = 0.5, suppressDays = 7, enabled = true } = {}) => {
  const [open, setOpenInternal] = useState(false);
  const firedRef = useRef(false);

  const fire = () => {
    if (firedRef.current) return;
    if (wasRecentlyShown(suppressDays)) return;
    firedRef.current = true;
    markShownNow();
    setOpenInternal(true);
  };

  useEffect(() => {
    if (!enabled) return;
    if (wasRecentlyShown(suppressDays)) return;

    const timer = setTimeout(fire, delayMs);

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 1;
      const total = doc.scrollHeight || 1;
      const denom = Math.max(total - viewport, 1);
      const progress = scrollTop / denom;
      if (progress >= scrollThreshold) fire();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [enabled, delayMs, scrollThreshold, suppressDays]);

  // Allow caller to also open it programmatically (e.g. via CTA button)
  // CTA-driven opens should NOT trigger 7-day suppression.
  const setOpen = (v) => setOpenInternal(v);

  return { open, setOpen };
};

export default useFloorPlanTrigger;
